import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "./Navbar";
import Footer from "./Footer";


import aboutImg from "../assets/luxprod.png";
import tradingImg from "../assets/cpt.png";
import webImg from "../assets/web.png";

import quality from "../assets/high-quality.png";
import proximity from "../assets/radar.png";
import reactivity from "../assets/motivation.png";

// Fonts
const clashDisplay = { fontFamily: "'Clash Display', sans-serif" };
const manrope = { fontFamily: "'Manrope', sans-serif" };

const CONFIG = {
  siteUrl: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 
           process.env.REACT_APP_SITE_URL || 
           "https://graphicup.tn",
};

// Service Section Component
const ServiceSection = ({ title, description, imageSrc, imageAlt, variant = "default", index }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const element = document.getElementById(`service-${index}`);
    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, [index]);

  const variants = {
    default: {
      contentOrder: "order-2 lg:order-1",
      imageOrder: "order-1 lg:order-2",
      gradient: "from-white to-gray-50",
    },
    reversed: {
      contentOrder: "order-2 lg:order-2",
      imageOrder: "order-1 lg:order-1",
      gradient: "from-gray-50 to-white",
    }
  };

  const config = variants[variant] || variants.default;

  return (
    <div 
      id={`service-${index}`}
      className={`relative flex flex-col lg:flex-row gap-x-15 lg:min-h-screen rounded-[60px] z-10 overflow-hidden bg-gradient-to-br ${config.gradient} max-w-8xl mx-auto px-6 py-8 sm:px-8 sm:py-10 lg:px-12 lg:py-8 transition-all duration-700 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      {/* Content */}
      <div className={`${config.contentOrder} relative flex flex-col justify-center items-start w-full lg:w-3/5 px-4 py-6 lg:px-12 lg:py-5 text-left text-black`}>
        {/* Decorative line */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-0.5 bg-gradient-to-r from-[#158DC1] to-transparent"></div>
          <span className="text-[#158DC1] text-sm font-semibold tracking-wider" style={clashDisplay}>
            A Propos de Nous
          </span>
          <div className="w-12 h-0.5 bg-gradient-to-r from-transparent to-[#158DC1]"></div>
        </div>
        
        <h2
          className="text-xl sm:text-2xl md:text-3xl lg:text-h2 font-bold mb-4"
          style={clashDisplay}
        >
          {title}
        </h2>
        <p className="text-base sm:text-lg md:text-lg mb-4 sm:mb-6 md:mb-8 lg:mb-10 max-w-[850px] leading-relaxed"
          style={manrope}
        >
          {description}
        </p>
      </div>

      {/* Image */}
      <div className={`${config.imageOrder} w-full lg:w-3/5 flex items-center justify-center lg:px-6 lg:py-20 sm:py-24 relative z-10`}>
        <img
          src={imageSrc}
          alt={imageAlt}
          className={`w-full h-auto ${
            index === 0 ? 'sm:max-w-[350px] sm:m-8 md:max-w-[500px] lg:min-w-[600px]' :
            index === 1 ? 'w-[80%]' :
            'max-w-[150px] sm:max-w-[300px] md:max-w-[400px] lg:max-w-[500px]'
          }`}
          loading="lazy"
        />
      </div>
    </div>
  );
};

// Back to Top Button
const BackToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (!isVisible) return null;

  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-8 right-8 z-50 p-4 bg-[#158DC1] text-white rounded-full shadow-2xl hover:scale-110 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-[#158DC1] focus:ring-opacity-50"
      aria-label="Retour en haut"
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
    </button>
  );
};

export default function Services() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const services = [
    {
      title: "À propos de notre société",
      description: "Graphic Up accompagne depuis plusieurs années les imprimeurs et professionnels des arts graphiques en leur fournissant des produits fiables et un savoir-faire reconnu. Nous mettons l'accent sur la qualité, l'innovation et la proximité avec nos clients.",
      imageSrc: webImg,
      imageAlt: "Notre société",
      variant: "default"
    },
    {
      title: "Négoce de matières premières pour Impression",
      description: "Nous distribuons encres, papiers, produits de dorure, pelliculages et autres consommables essentiels aux imprimeurs. Notre objectif est de garantir un approvisionnement constant et des produits de haute qualité adaptés à vos projets.",
      imageSrc: tradingImg,
      imageAlt: "Trading Offset",
      variant: "reversed"
    },
    {
      title: "Soustraitance d'activités graphiques",
      description: "En plus de notre activité principale, nous proposons des services de soustraitance : création de sites web, développement de solutions digitales et bientôt maintenance. Nous permettons à nos clients de se concentrer sur leur cœur de métier grâce à des solutions clés en main.",
      imageSrc: aboutImg,
      imageAlt: "Soustraitance",
      variant: "default"
    }
  ];

  const values = [
    {
      image: quality,
      title: "Qualité",
      description: "Produits premium sélectionnés auprès des meilleurs fabricants mondiaux"
    },
    {
      image: proximity,
      title: "Proximité",
      description: "Accompagnement personnalisé et écoute attentive de vos besoins"
    },
    {
      image: reactivity,
      title: "Réactivité",
      description: "Service rapide et fiable pour garantir la continuité de votre production"
    }
  ];

  return (
          <div className="w-full bg-black relative overflow-x-hidden">
      <Helmet>
        <html lang="fr" />
        <title>Nos Services – Graphic Up | Négoce et Expertise Arts Graphiques</title>
        <meta name="description" content="Découvrez nos services : négoce de matières premières pour impression offset, soustraitance d'activités graphiques et solutions digitales. Expertise et qualité depuis plusieurs années." />
        <link rel="canonical" href={`${CONFIG.siteUrl}/services`} />
        <meta property="og:title" content="Nos Services – Graphic Up" />
        <meta property="og:description" content="Négoce de matières premières pour impression, soustraitance graphique et solutions digitales" />
        <meta property="og:url" content={`${CONFIG.siteUrl}/services`} />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Skip to main content */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-white p-2 z-50 rounded-lg font-bold"
      >
        Aller au contenu principal
      </a>

      <Navbar />

      {/* Hero Section */}
      <section 
        className="relative h-[87vh] w-full flex items-center justify-center text-center text-white overflow-hidden"
        aria-label="Section héros"
      >
        <div className="absolute inset-0"></div>
        <div className={`relative z-10 mt-10 md:mt-24 max-w-6xl px-4 transition-all duration-1000 ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <h3
            className="text-xl sm:text-2xl md:text-h3 font-bold text-white mb-2"
            style={clashDisplay}
          >
            Graphic Up
          </h3>

          <h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-h1 font-bold text-white m-4 leading-tight"
            style={{...clashDisplay, textShadow: '0 2px 20px rgba(0,0,0,0.5)'}}
          >
            Nos Services
          </h1>

          <p
            className="text-base sm:text-lg md:text-xl lg:text-2xl italic text-white max-w-3xl mx-auto leading-relaxed"
            style={{...manrope, textShadow: '0 2px 15px rgba(0,0,0,0.5)'}}
          >
            Découvrez notre expertise, nos activités de négoce et nos solutions digitales.
          </p>
        </div>
      </section>

      {/* Services Sections */}
      <main id="main-content" className="relative z-10" aria-label="Nos services">
        {services.map((service, index) => (
          <div key={index}>
            <div className={index === 0 ? "" : "mt-28"}>
              <ServiceSection {...service} index={index} />
            </div>
            
            {/* Insert "Nos Valeurs" after second service */}
            {index === 1 && (
              <section className="relative py-20 px-6 mt-28 bg-black">
                <div className="max-w-7xl mx-auto text-center">
                  {/* Decorative line */}
                  <div className="flex items-center justify-center gap-4 mb-10">
                    <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#158DC1] to-transparent"></div>
                    <span className="text-white/70 text-sm font-semibold tracking-wider" style={clashDisplay}>
                      NOS VALEURS
                    </span>
                    <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#158DC1] to-transparent"></div>
                  </div>
                  
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4" style={clashDisplay}>
                    Pourquoi{" "}
                    <span className="text-[#158DC1]">
                      Graphic Up
                    </span>
                    {" "}?
                  </h2>
                  <p className="text-lg md:text-xl text-white/70 mb-12 max-w-3xl mx-auto" style={manrope}>
                    Ce qui nous différencie et guide notre engagement envers vous
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-10">
                    {values.map((value, idx) => (
                      <div 
                        key={idx} 
                        className="bg-white rounded-3xl p-8 shadow-2xl transition-all duration-300"
                        style={{
                          animation: 'fadeInUp 0.6s ease-out both',
                          animationDelay: `${idx * 0.15}s`
                        }}
                      >
                        <img 
                          src={value.image} 
                          alt={value.title}
                          className="w-20 h-20 mx-auto mb-6 object-contain"
                        />
                        <h3 className="text-xl font-bold text-black mb-3" style={clashDisplay}>
                          {value.title}
                        </h3>
                        <p className="text-gray-700 text-base leading-relaxed" style={manrope}>
                          {value.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </div>
        ))}

        {/* CTA Section */}
        <section className="relative py-20 px-6 mt-28 bg-black">
          <div className="max-w-4xl mx-auto text-center">
            {/* Decorative line */}
            <div className="flex items-center justify-center gap-4 mb-10">
              <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#158DC1] to-transparent"></div>
              <span className="text-white/70 text-sm font-semibold tracking-wider" style={clashDisplay}>
                PRÊT À COLLABORER ?
              </span>
              <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#158DC1] to-transparent"></div>
            </div>
            
            {/* Main heading */}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6" style={clashDisplay}>
              Donnez vie à vos <span className="text-[#158DC1]">projets</span>
            </h2>
            
            {/* Description */}
            <p className="text-lg text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed" style={manrope}>
              Contactez-nous dès aujourd'hui pour discuter de vos besoins et découvrir comment Graphic Up peut vous accompagner.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <a href="/Contact">
                <button 
                  className="px-10 py-3.5 text-lg min-w-[220px] bg-[#158DC1] text-white hover:bg-white hover:text-[#158DC1] rounded-[15px] border-2 border-transparent hover:border-[#158DC1] transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                  style={clashDisplay}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Nous contacter
                </button>
              </a>
              
              <a href="/produits">
                <button 
                  className="px-10 py-3.5 text-lg min-w-[220px] border-2 border-white/30 text-white hover:bg-white hover:text-black hover:border-white rounded-[15px] transition-all duration-300 flex items-center justify-center gap-2"
                  style={clashDisplay}
                >
                  Voir nos produits
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </a>
            </div>
          </div>
        </section>
      </main>

      <BackToTop />

      <div className="w-full h-24 -z-40 bg-black"></div>
      <Footer />

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        html {
          scroll-behavior: smooth;
        }

        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
          
          html {
            scroll-behavior: auto;
          }
        }

        button:focus-visible,
        a:focus-visible {
          outline: 2px solid #158DC1;
          outline-offset: 2px;
        }
      `}</style>
    </div>
  );
}